import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Download, BookmarkPlus, Loader2, Check, Quote as QuoteIcon, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf, formatErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoomBreakdown, TotalsCard, PaymentSchedule } from "@/components/quote/QuoteParts";
import MoodBoard from "@/components/quote/MoodBoard";
import BookVisit from "@/components/quote/BookVisit";

const Btn = ({ onClick, busy, icon: Icon, children, primary, testid }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-60 ${primary ? "bg-brand-accent hover:bg-brand-accent-dark text-white" : "border border-brand-ink/20 hover:border-brand-accent hover:text-brand-accent"}`}>
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {children}
  </button>
);

export default function QuotePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    const pending = q && (q.mood_board?.status === "generating" || q.mood_board_variants?.status === "generating");
    if (!pending || polls.current > 40) return;
    const t = setTimeout(() => { polls.current += 1; api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => {}); }, 3000);
    return () => clearTimeout(t);
  }, [q, id]);

  useEffect(() => { if (params.get("book") && q) setTimeout(() => document.getElementById("book-visit")?.scrollIntoView({ behavior: "smooth" }), 400); }, [params, q]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(formatErr(e)); }
    setBusy("");
  };
  const pdf = () => run("pdf", () => downloadPdf(q.id, q.quote_number), "Quotation PDF downloaded");
  const email = () => run("email", async () => { const { data } = await api.post(`/quotes/${id}/email`); toast.success(`Quotation emailed to ${data.to}`); });
  const save = () => (!user ? nav(`/login?next=/quote/${id}&save=1`) : run("save", async () => setQ((await api.post(`/quotes/${id}/save`)).data), "Saved to your account"));
  const switchTier = (t) => run(`tier-${t}`, async () => { polls.current = 0; setQ((await api.post(`/quotes/${id}/package`, { tier: t })).data); }, "Package updated — repricing & new mood board");
  const setQuote = (data) => { polls.current = 0; setQ(data); };
  const whatsapp = () => {
    const msg = `HOMIIN Interiors quotation ${q.quote_number}\n${q.tier_info.label} package · ${q.rooms.length} rooms · ${q.total_area} sq.ft\nEstimated total: ${inr(q.total)} (incl. GST)\n\nView online & download the PDF: ${window.location.origin}/quote/${q.id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  if (!q) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;
  const owned = user && q.user_id === user.id;
  const canEdit = !q.user_id || owned || user?.role === "admin";

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="quote-page">
      <MoodBoard q={q} setQ={setQuote} canEdit={canEdit} />

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-brand-line pb-8 mt-10">
        <div>
          <p className="font-mono-num text-xs text-brand-accent" data-testid="quote-number">{q.quote_number}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink mt-3">{q.client.name.split(" ")[0]}'s {q.tier_info.label} Home</h1>
          <p className="text-brand-muted mt-3 text-sm">{q.rooms.length} rooms · {q.total_area} sq.ft · {q.source === "ai" ? "AI floor plan analysis" : "Manual estimate"} · {new Date(q.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Btn onClick={pdf} busy={busy === "pdf"} icon={Download} primary testid="download-pdf-btn">Download PDF</Btn>
          <Btn onClick={email} busy={busy === "email"} icon={Mail} testid="resend-email-btn">Resend to my email</Btn>
          <Btn onClick={whatsapp} icon={MessageCircle} testid="whatsapp-share-btn">Share on WhatsApp</Btn>
          {owned ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-line px-6 py-3 text-sm text-brand-muted" data-testid="quote-saved-badge"><Check className="w-4 h-4 text-brand-accent" /> Saved</span>
          ) : (
            <Btn onClick={save} busy={busy === "save"} icon={BookmarkPlus} testid="save-quote-btn">{user ? "Save to my account" : "Sign in to save"}</Btn>
          )}
        </div>
      </div>

      {pricing && canEdit && (
        <div className="flex flex-wrap items-center gap-3 mt-6" data-testid="package-switcher">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-muted">Package</span>
          {Object.entries(pricing.tiers).map(([k, t]) => (
            <button key={k} onClick={() => k !== q.tier && switchTier(k)} disabled={!!busy} data-testid={`switch-tier-${k}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors inline-flex items-center gap-2 ${q.tier === k ? "bg-brand-ink text-brand-cream border-brand-ink" : "border-brand-line hover:border-brand-accent"}`}>
              {busy === `tier-${k}` && <Loader2 className="w-3 h-3 animate-spin" />}{t.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 space-y-8">
          {q.ai_summary && (
            <div className="rounded-2xl bg-brand-sand p-7 flex gap-4" data-testid="quote-ai-note">
              <QuoteIcon className="w-6 h-6 text-brand-accent shrink-0" />
              <div><p className="text-xs uppercase tracking-[0.2em] text-brand-accent">Designer's note</p><p className="font-serif-display text-xl text-brand-ink mt-2 leading-snug">{q.ai_summary}</p></div>
            </div>
          )}
          <RoomBreakdown rooms={q.rooms} />
          {q.addons.length > 0 && (
            <div className="rounded-2xl border border-brand-line p-6" data-testid="quote-addons">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent mb-3">Add-ons</p>
              {q.addons.map((a) => <div key={a.key} className="flex justify-between text-sm py-2"><span className="text-brand-muted">{a.name}</span><span className="font-mono-num">{inr(a.amount)}</span></div>)}
            </div>
          )}
        </div>
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start">
          <TotalsCard q={q} />
          <BookVisit q={q} open={!!params.get("book")} />
          <div className="rounded-2xl border border-brand-line p-6 text-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-accent mb-2">Materials</p>
            <p className="text-brand-muted leading-relaxed">{q.tier_info.materials}</p>
          </div>
          <PaymentSchedule items={q.payment_schedule} />
          <Link to="/estimate" className="block text-center text-sm text-brand-accent hover:text-brand-accent-dark" data-testid="new-estimate-link">Create another estimate →</Link>
        </aside>
      </div>
    </section>
  );
}
